import {
  ButtonHTMLAttributes,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/* ------------------------------------------------------------------ */
/*  Icons — small hand-tuned stroke set (16/18px)                      */
/* ------------------------------------------------------------------ */

type IconProps = { size?: number; strokeWidth?: number; className?: string };

function base(size: number) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
}

export const Icon = {
  Dashboard: ({ size = 17, strokeWidth = 1.9 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <rect x="3.5" y="3.5" width="7" height="9" rx="1.6" />
      <rect x="13.5" y="3.5" width="7" height="5" rx="1.6" />
      <rect x="13.5" y="11.5" width="7" height="9" rx="1.6" />
      <rect x="3.5" y="15.5" width="7" height="5" rx="1.6" />
    </svg>
  ),
  Pin: ({ size = 17, strokeWidth = 1.9 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <path d="M12 21s7-5.3 7-11a7 7 0 1 0-14 0c0 5.7 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.6" />
    </svg>
  ),
  Activity: ({ size = 17, strokeWidth = 1.9 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <path d="M3 12h4l2.5-7 4 14 2.5-7H21" />
    </svg>
  ),
  Plug: ({ size = 17, strokeWidth = 1.9 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <path d="M9 7V3.5M15 7V3.5" />
      <path d="M6.5 7h11v4a5.5 5.5 0 0 1-11 0V7Z" />
      <path d="M12 16.5V21" />
    </svg>
  ),
  Card: ({ size = 17, strokeWidth = 1.9 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <rect x="3" y="5.5" width="18" height="13" rx="2.2" />
      <path d="M3 10h18" />
      <path d="M7 14.5h4" />
    </svg>
  ),
  Palette: ({ size = 17, strokeWidth = 1.9 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <path d="M12 3a9 9 0 1 0 0 18h1.6a2.2 2.2 0 0 0 1.6-3.7c-.9-1-.2-2.6 1.2-2.6H19a2 2 0 0 0 2-2A9.4 9.4 0 0 0 12 3Z" />
      <circle cx="8" cy="10" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="7.5" r="1" fill="currentColor" stroke="none" />
      <circle cx="16" cy="10" r="1" fill="currentColor" stroke="none" />
    </svg>
  ),
  Users: ({ size = 17, strokeWidth = 1.9 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <circle cx="9" cy="8.5" r="3.2" />
      <path d="M3.5 19.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      <path d="M15.5 5.8a3.2 3.2 0 0 1 0 5.4M17.5 14.9c1.8.7 3 2.2 3 4.6" />
    </svg>
  ),
  Shield: ({ size = 17, strokeWidth = 1.9 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <path d="M12 3 5 5.8v5.4c0 4.5 3 7.7 7 9.3 4-1.6 7-4.8 7-9.3V5.8L12 3Z" />
      <path d="m9.2 11.8 2 2 3.6-4" />
    </svg>
  ),
  Check: ({ size = 16, strokeWidth = 2.4 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  ),
  X: ({ size = 15, strokeWidth = 2.1 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  ),
  Alert: ({ size = 16, strokeWidth = 2 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <path d="M12 4 2.8 19.5h18.4L12 4Z" />
      <path d="M12 10v4.2" />
      <circle cx="12" cy="16.8" r="0.4" fill="currentColor" />
    </svg>
  ),
  Info: ({ size = 16, strokeWidth = 2 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5" />
      <circle cx="12" cy="8" r="0.4" fill="currentColor" />
    </svg>
  ),
  Plus: ({ size = 16, strokeWidth = 2.2 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  Search: ({ size = 16, strokeWidth = 2 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4.5 4.5" />
    </svg>
  ),
  External: ({ size = 14, strokeWidth = 2 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <path d="M14 4h6v6" />
      <path d="M20 4 11 13" />
      <path d="M19 13.5V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h4.5" />
    </svg>
  ),
  Copy: ({ size = 15, strokeWidth = 1.9 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4.5A1.5 1.5 0 0 1 3 13.5v-9A1.5 1.5 0 0 1 4.5 3h9A1.5 1.5 0 0 1 15 4.5V5" />
    </svg>
  ),
  Chevron: ({ size = 15, strokeWidth = 2.1, className }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth} className={className}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  ),
  Refresh: ({ size = 15, strokeWidth = 2 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <path d="M20 11.5A8 8 0 1 0 18.6 16" />
      <path d="M20 5v6.5h-6.5" />
    </svg>
  ),
  Play: ({ size = 15, strokeWidth = 2 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <path d="M7 4.8v14.4L19 12 7 4.8Z" />
    </svg>
  ),
  Archive: ({ size = 15, strokeWidth = 1.9 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <rect x="3" y="4" width="18" height="5" rx="1.2" />
      <path d="M5 9v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9" />
      <path d="M10 13h4" />
    </svg>
  ),
  Logout: ({ size = 16, strokeWidth = 1.9 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <path d="M14 4H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7" />
      <path d="M17 8.5 20.5 12 17 15.5" />
      <path d="M20.5 12H10" />
    </svg>
  ),
  Help: ({ size = 16, strokeWidth = 2 }: IconProps) => (
    <svg {...base(size)} strokeWidth={strokeWidth}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9.6 9.3A2.5 2.5 0 0 1 14.4 10c0 1.6-2.4 2-2.4 3.4" />
      <circle cx="12" cy="16.6" r="0.4" fill="currentColor" />
    </svg>
  ),
  Google: ({ size = 16 }: IconProps) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M21.6 12.2c0-.7-.06-1.4-.18-2H12v3.9h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.4Z"
        fill="#4285F4"
      />
      <path
        d="M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22Z"
        fill="#34A853"
      />
      <path
        d="M6.4 14a6 6 0 0 1 0-3.8V7.6H3.1a10 10 0 0 0 0 9l3.3-2.6Z"
        fill="#FBBC05"
      />
      <path
        d="M12 6.1c1.5 0 2.8.5 3.8 1.5L18.7 5A10 10 0 0 0 3.1 7.6l3.3 2.6C7.2 7.9 9.4 6.1 12 6.1Z"
        fill="#EA4335"
      />
    </svg>
  ),
};

/* ------------------------------------------------------------------ */
/*  Toasts                                                             */
/* ------------------------------------------------------------------ */

type ToastVariant = 'success' | 'error' | 'info';

type ToastItem = {
  id: number;
  variant: ToastVariant;
  title: string;
  description?: string;
};

type ToastContextValue = {
  toast: (input: { variant?: ToastVariant; title: string; description?: string }) => void;
};

const ToastContext = createContext<ToastContextValue>({ toast: () => undefined });

export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const toast = useCallback(
    (input: { variant?: ToastVariant; title: string; description?: string }) => {
      const id = ++counter.current;
      setItems((current) => [
        ...current.slice(-3),
        { id, variant: input.variant ?? 'success', title: input.title, description: input.description },
      ]);
      window.setTimeout(() => dismiss(id), 5200);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {items.map((item) => (
          <div key={item.id} className={`toast ${item.variant}`}>
            <span className="toast-icon">
              {item.variant === 'success' && <Icon.Check size={14} />}
              {item.variant === 'error' && <Icon.Alert size={14} />}
              {item.variant === 'info' && <Icon.Info size={14} />}
            </span>
            <div className="toast-body">
              <strong>{item.title}</strong>
              {item.description && <span>{item.description}</span>}
            </div>
            <button className="toast-close" onClick={() => dismiss(item.id)} aria-label="Dismiss notification">
              <Icon.X size={13} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
/*  Button — built-in loading spinner and "saved" confirmation state    */
/* ------------------------------------------------------------------ */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'md' | 'sm';
  loading?: boolean;
  loadingText?: string;
  saved?: boolean;
  savedText?: string;
  icon?: ReactNode;
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  loadingText,
  saved = false,
  savedText = 'Saved',
  icon,
  children,
  className = '',
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      className={`btn ${variant} ${size === 'sm' ? 'sm' : ''} ${saved ? 'is-saved' : ''} ${className}`}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? (
        <>
          <span className="spinner" aria-hidden="true" />
          {loadingText ?? children}
        </>
      ) : saved ? (
        <>
          <span className="btn-check" aria-hidden="true">
            <Icon.Check size={15} />
          </span>
          {savedText}
        </>
      ) : (
        <>
          {icon}
          {children}
        </>
      )}
    </button>
  );
}

/**
 * Tracks a short-lived "saved" flag to flash success state on buttons
 * after a mutation completes.
 */
export function useSavedFlag(durationMs = 1800): [boolean, () => void] {
  const [saved, setSaved] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const trigger = useCallback(() => {
    setSaved(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setSaved(false), durationMs);
  }, [durationMs]);

  return [saved, trigger];
}

/* ------------------------------------------------------------------ */
/*  Status badge — translates backend enums into friendly labels        */
/* ------------------------------------------------------------------ */

const STATUS_MAP: Record<string, { label: string; tone: string; live?: boolean }> = {
  ACTIVE: { label: 'Active', tone: 'success' },
  INACTIVE: { label: 'Paused', tone: 'warning' },
  DELETED: { label: 'Archived', tone: 'neutral' },
  QUEUED: { label: 'Queued', tone: 'info' },
  RUNNING: { label: 'Running', tone: 'info', live: true },
  RETRYING: { label: 'Retrying', tone: 'warning', live: true },
  COMPLETED: { label: 'Completed', tone: 'success' },
  PARTIAL_SUCCESS: { label: 'Partly done', tone: 'warning' },
  FAILED: { label: 'Failed', tone: 'danger' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
  PENDING: { label: 'Pending', tone: 'neutral' },
  SKIPPED: { label: 'Skipped', tone: 'neutral' },
  LIVE: { label: 'Published', tone: 'success' },
  TRIALING: { label: 'Free trial', tone: 'info' },
  PAST_DUE: { label: 'Payment overdue', tone: 'danger' },
  CANCELED: { label: 'Cancelled', tone: 'neutral' },
  INCOMPLETE: { label: 'Incomplete', tone: 'warning' },
  UNPAID: { label: 'Unpaid', tone: 'danger' },
  INVITED: { label: 'Invite sent', tone: 'info' },
  DISABLED: { label: 'Disabled', tone: 'neutral' },
  BLOCKED: { label: 'Blocked', tone: 'danger' },
};

export function StatusBadge({ status, live }: { status?: string | null; live?: boolean }) {
  if (!status) {
    return <span className="badge neutral">Unknown</span>;
  }

  const entry = STATUS_MAP[status] ?? { label: status, tone: 'neutral' };
  return (
    <span className={`badge ${entry.tone}`}>
      <span className={`dot ${entry.live || live ? 'live' : ''}`} aria-hidden="true" />
      {entry.label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Alerts, skeletons, empty states                                     */
/* ------------------------------------------------------------------ */

export function InlineAlert({
  variant = 'info',
  children,
}: {
  variant?: 'success' | 'danger' | 'info' | 'warning';
  children: ReactNode;
}) {
  return (
    <div className={`alert ${variant}`} role={variant === 'danger' ? 'alert' : 'status'}>
      {variant === 'success' && <Icon.Check size={15} />}
      {variant === 'danger' && <Icon.Alert size={15} />}
      {variant === 'warning' && <Icon.Alert size={15} />}
      {variant === 'info' && <Icon.Info size={15} />}
      <div>{children}</div>
    </div>
  );
}

export function Skeleton({ height = 16, width, style }: { height?: number; width?: number | string; style?: React.CSSProperties }) {
  return <div className="skeleton" style={{ height, width: width ?? '100%', ...style }} aria-hidden="true" />;
}

export function SkeletonList({ rows = 3, height = 58 }: { rows?: number; height?: number }) {
  return (
    <div className="grid" aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} height={height} />
      ))}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <span className="empty-icon">{icon ?? <Icon.Info size={20} />}</span>
      <h4>{title}</h4>
      {description && <p>{description}</p>}
      {action}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div>
        <h2>{title}</h2>
        {description && <p className="page-sub">{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Confirm dialog                                                      */
/* ------------------------------------------------------------------ */

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  tone = 'danger',
  loading = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  tone?: 'danger' | 'primary';
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    if (!open) {
      return;
    }

    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onCancel();
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} onClick={(event) => event.stopPropagation()}>
        <h3>{title}</h3>
        <p>{body}</p>
        <div className="modal-actions">
          <Button variant="ghost" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Copy-to-clipboard chip                                              */
/* ------------------------------------------------------------------ */

export function CopyChip({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="copy-chip">
      <code title={value}>{label ?? value}</code>
      <Button
        variant="secondary"
        size="sm"
        saved={copied}
        savedText="Copied"
        icon={<Icon.Copy size={13} />}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
          } catch {
            const helper = document.createElement('textarea');
            helper.value = value;
            document.body.appendChild(helper);
            helper.select();
            document.execCommand('copy');
            helper.remove();
          }
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1600);
        }}
      >
        Copy
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Field — label + optional hint                                       */
/* ------------------------------------------------------------------ */

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label>
      <span>{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}
